// Angular
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, DOCUMENT } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from "@angular/router";

// RxJS
import { Subject, filter } from "rxjs";
import { concatMap, take, takeUntil } from 'rxjs/operators';
// Services
import { SeoService } from "./core/_base/utils/seo.service";
import { ThemeModeService } from './views/pages/layout/services';
import { LazyLoadScriptService } from "./core/_base/utils";
// Environments
import { environment } from "@environment";
// Components
import { CookiePopupComponent, GoogleAnalyticsComponent, ZendeskWidgetComponent, ErrorNotificationBannerComponent } from "./core/_base/layout";
import { ChatUserListComponent } from "./chat/chat-user-list/chat-user-list.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, CookiePopupComponent, GoogleAnalyticsComponent, ZendeskWidgetComponent, ErrorNotificationBannerComponent, ChatUserListComponent]
})
export class AppComponent implements OnInit, OnDestroy {

  // Private
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private seoService = inject(SeoService);
  private themeModeService = inject(ThemeModeService);
  private document = inject<Document>(DOCUMENT);
  private lazyLoadScriptService = inject(LazyLoadScriptService);
  private destroy$ = new Subject<void>();

  /**
   * Creates an instance of AppComponent.
   * @memberof AppComponent
   */
  constructor() {
    this.setFavicon();
  }

  /**
   * On Init
   * @memberof AppComponent
   */
  ngOnInit() {
    this.themeModeService.init();
    this.setPageTitleOnNavigation();
    this.loadWebmcpScript();
  }

  /**
   * On Destroy
   * @memberof AppComponent
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Set Favicons
   * @private
   * @memberof AppComponent
   */
  private setFavicon(): void {
    const faviconIco = this.document.querySelector('#appFaviconIco') as HTMLLinkElement;
    const favicon32 = this.document.querySelector('#appFavicon32') as HTMLLinkElement;
    const favicon48 = this.document.querySelector('#appFavicon48') as HTMLLinkElement;
    const favicon96 = this.document.querySelector('#appFavicon96') as HTMLLinkElement;
    const appleTouchIcon = this.document.querySelector('#appAppleTouchIcon') as HTMLLinkElement;

    faviconIco?.setAttribute('href', environment.favicon.ico);
    favicon32?.setAttribute('href', environment.favicon.png32);
    favicon48?.setAttribute('href', environment.favicon.png48);
    favicon96?.setAttribute('href', environment.favicon.png96);
    appleTouchIcon?.setAttribute('href', environment.favicon.appleTouchIcon);
  }

  /**
   * Set Page Title OnNavigation
   * @private
   * @memberof AppComponent
   */
  private setPageTitleOnNavigation(): void {
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => this.updatePageTitle());
  }

  /**
   * Load Web MCP Script and Polyfill
   * @private
   * @return {*}  {void}
   * @memberof AppComponent
   */
  private loadWebmcpScript(): void {
    if (!environment.webmcp?.isEnabled) return;
    this.lazyLoadScriptService.loadScript(environment.webmcp?.scriptPolyfillUrl, 'webmcp-polyfill-script', 'head').pipe(take(1)).subscribe();
    this.lazyLoadScriptService.loadScript(environment.webmcp?.scriptUrl, 'webmcp-script', 'head').pipe(take(1)).subscribe();
  }
  /**
   * Update Page Title
   * @private
   * @memberof AppComponent
   */
  private updatePageTitle(): void {
    const route = this.getLastChildRoute(this.activatedRoute.snapshot);
    const skipDefaultTitle = route?.data?.skipDefaultTitle;

    if (!skipDefaultTitle) {
      this.seoService.setPage(route?.data?.title);
    }
  }

  /**
   * Get Last Child Route
   * @private
   * @param {*} route
   * @returns {*}
   * @memberof AppComponent
   */
  private getLastChildRoute(route: any): any {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

}
